// Shared Maeve chat bubble (Brand & Design Guidelines §7 + User Journeys v2).
// One implementation for every conversational surface — previously the same
// bubble was hand-rolled in HrMaeveChat, EmployeeCompanionChat, and the
// Manager guidance/touch-point-prep surfaces (which had drifted onto an older
// bordered style).
//
// Colours per surface:
//   standard (manager / HR) — user: Ink navy / white · Maeve: Teal Light / navy
//   employee (companion)    — user: Employee Blue / white · Maeve: Blue Light / navy
//
// Asymmetric radius: 14px with a 3px tail on the send corner (bottom-right for
// the right-indented user bubble, bottom-left for Maeve). The header line
// ("You" / "Maeve") keeps the turn attributable without colour alone (WCAG
// 1.4.1); an optional `badge` slot carries the Companion Amber "noted" pill.
//
// Maeve's own turns render through `renderMaeveMarkdown` (bold/list markdown
// the model reaches for, e.g. a structured case-list answer) — a user's own
// typed text is always shown exactly as written, never reinterpreted.
//
// Memoized: every conversational surface renders a `.map()` list of these,
// and the transcript's parent component also owns the (frequently-changing,
// e.g. on every keystroke of a draft reply) input state — without memo, every
// bubble in the list re-renders and re-runs `renderMaeveMarkdown` on every
// prior turn's text on every parent re-render, not just when a new turn is
// appended. `badge` is deliberately excluded from the comparison: it's a
// fresh JSX element each render (new object identity even when visually
// identical), but it's derived from the same immutable turn data as
// `children`/`role` — if those haven't changed, the badge hasn't either.

import { memo, type ReactNode } from 'react';
import { renderMaeveMarkdown } from './renderMaeveMarkdown.tsx';

interface ChatBubbleProps {
  role: 'user' | 'assistant';
  children: ReactNode;
  /** `employee` uses the softer blue companion palette. */
  variant?: 'standard' | 'employee';
  /** Extra node in the Maeve header line (e.g. the Amber "noted" badge). */
  badge?: ReactNode;
}

function ChatBubbleImpl({ role, children, variant = 'standard', badge }: ChatBubbleProps) {
  const isUser = role === 'user';
  const content =
    !isUser && typeof children === 'string' ? renderMaeveMarkdown(children) : children;
  // Employee user bubble uses the AA-compliant -action shade (white text on
  // the light employee blue is only ~2.6:1 — see index.css).
  const userBg =
    variant === 'employee' ? 'bg-employee-blue-action text-white' : 'bg-ink text-white';
  const maeveBg = variant === 'employee' ? 'bg-blue-light text-navy' : 'bg-teal-light text-navy';
  return (
    <article
      className={[
        'p-3 text-sm whitespace-pre-wrap',
        isUser
          ? `ml-6 rounded-[14px] rounded-br-[3px] ${userBg}`
          : `mr-6 rounded-[14px] rounded-bl-[3px] ${maeveBg}`,
      ].join(' ')}
    >
      <header
        className={[
          'mb-1 flex items-center gap-2 text-xs font-medium tracking-wide uppercase',
          isUser ? 'text-white/70' : 'text-muted-foreground',
        ].join(' ')}
      >
        <span>{isUser ? 'You' : 'Maeve'}</span>
        {!isUser && badge ? badge : null}
      </header>
      {isUser ? <p>{content}</p> : content}
    </article>
  );
}

export const ChatBubble = memo(
  ChatBubbleImpl,
  (prev, next) =>
    prev.role === next.role && prev.children === next.children && prev.variant === next.variant,
);

// Maeve's "…" while a turn is in flight, matching the Manager messaging-app
// surface's `PendingBubble` (same dots, same colours) in this document-style
// bubble's own shape. Purely a progress indicator for a request/response call
// (no token streaming — the safety pipeline must classify the whole turn
// before any of it is shown), so it is NOT a "typing" simulation and is
// announced to assistive tech as a status, not as speech.
export function ChatPendingBubble({ variant = 'standard' }: { variant?: 'standard' | 'employee' }) {
  const maeveBg = variant === 'employee' ? 'bg-blue-light' : 'bg-teal-light';
  return (
    <div
      role="status"
      aria-label="Maeve is preparing a response"
      className={`mr-6 flex w-fit gap-1 rounded-[14px] rounded-bl-[3px] ${maeveBg} px-4 py-3.5`}
    >
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          aria-hidden
          className="size-1.5 animate-pulse rounded-full bg-navy/35"
          style={{ animationDelay: `${String(delay)}ms` }}
        />
      ))}
    </div>
  );
}
